import {CouponInfoSheet} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  ContentPopper,
  ContentPopperProps,
  SecondaryContentPopperButton,
  Text,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {CouponInfoHeader} from './CoupontInfoHeader';
import {useCouponInfoContentPopperStateManager} from './useCouponInfoContentPopperStateManager';

interface CouponInfoPopperProps {
  LayoutComponent: ContentPopperProps['LayoutComponent'];
  couponInfo?: CouponInfoSheet;
  translations?: {
    badgeText: string;
    title: string;
    content: string;
    closeButtonText?: string;
  };
  hideBadgeIcon?: boolean;
  ['data-testid']?: string;
  className?: string;
  stateManagerCustomKey?: string;
  onClose?: VoidFunction;
}

export const CouponInfoContentPopper: React.FC<CouponInfoPopperProps> = ({
  couponInfo,
  ...otherProps
}) => {
  if (!couponInfo) {
    return <CouponInfoContentPopperBase {...otherProps} />;
  }

  return (
    <CouponInfoContentPopperBase
      {...otherProps}
      translations={{
        badgeText: couponInfo?.badgeText || '',
        title: couponInfo?.headline || '',
        content: couponInfo?.body || '',
      }}
      hideBadgeIcon
    />
  );
};

const CouponInfoContentPopperBase: React.FC<
  Omit<CouponInfoPopperProps, 'coupon'>
> = ({
  LayoutComponent,
  translations,
  hideBadgeIcon,
  'data-testid': dataTestId,
  className,
  stateManagerCustomKey,
  onClose,
}) => {
  const {isOpen, cancel} = useCouponInfoContentPopperStateManager(
    stateManagerCustomKey,
  );
  const {t} = useTranslation();

  const handleOnClose = () => {
    cancel();
    onClose?.();
  };

  return (
    <ContentPopper
      LayoutComponent={LayoutComponent}
      className={className}
      renderInPortal
      data-testid={`${dataTestId}-contentPopper`}
      open={isOpen}
      onClose={handleOnClose}
      headerContent={
        <CouponInfoHeader
          badgeText={translations?.badgeText || ''}
          title={translations?.title || ''}
          hideBadgeIcon={hideBadgeIcon}
          data-testid={dataTestId}
        />
      }
      bodyContent={<BodyText as="p">{translations?.content || ''}</BodyText>}
      footerContent={
        <SecondaryContentPopperButton
          aria-label={undefined}
          data-testid={`${dataTestId}-CloseButton`}
          label={translations?.closeButtonText || t('Close')}
          onClick={handleOnClose}
        />
      }
    />
  );
};

const BodyText = styled(Text)`
  line-height: 1.5;
`;
